import React from 'react';
import { AppError, ErrorAction } from '@/services/errors';
import { Button } from './Button';

interface ErrorDisplayProps {
  error: AppError;
  onDismiss?: () => void;
  className?: string;
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  onDismiss,
  className = ''
}) => {
  const getErrorIcon = (code: string) => {
    switch (code) {
      case 'NETWORK_ERROR':
        return '🌐';
      case 'TIMEOUT_ERROR':
        return '⏱️';
      case 'RATE_LIMIT_ERROR':
        return '⚠️';
      case 'AUTH_ERROR':
        return '🔐';
      case 'VALIDATION_ERROR':
        return '❌';
      case 'NOT_FOUND_ERROR':
        return '🔍';
      case 'SERVER_ERROR':
        return '🔧';
      default:
        return '⚠️';
    }
  };

  const getErrorColor = (code: string) => {
    switch (code) {
      case 'NETWORK_ERROR':
      case 'TIMEOUT_ERROR':
        return 'border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-900/20';
      case 'RATE_LIMIT_ERROR':
        return 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20';
      case 'AUTH_ERROR':
        return 'border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-900/20';
      case 'VALIDATION_ERROR':
        return 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20';
      case 'SERVER_ERROR':
        return 'border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20';
      default:
        return 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20';
    }
  };

  return (
    <div
      className={`
        border rounded-lg p-4 ${getErrorColor(error.code)} ${className}
      `}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <div className="text-xl" aria-hidden="true">
          {getErrorIcon(error.code)}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {error.userMessage}
              </h4>
              
              {error.context && (
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  Context: {error.context}
                </p>
              )}
              
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                {new Date(error.timestamp).toLocaleTimeString()}
              </p>
            </div>
            
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors z-10"
                aria-label="Dismiss error"
                type="button"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
          </div>
          
          {error.actions && error.actions.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {error.actions.map((action: ErrorAction, index: number) => (
                <Button
                  key={index}
                  size="sm"
                  variant={action.primary ? 'primary' : 'secondary'}
                  onClick={action.action}
                  className="text-xs"
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};